import { Plus, Trash } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { TestThreshold } from "@/types/test";
import { nextThreshold } from "./threshold-defaults";

interface Props {
  thresholds: TestThreshold[];
  maxScore: number;
  onChange: (next: TestThreshold[]) => void;
}

export function TestThresholdEditor({ thresholds, maxScore, onChange }: Props) {
  const { t } = useTranslation();

  const update = (idx: number, patch: Partial<TestThreshold>) => {
    const next = [...thresholds];
    next[idx] = { ...next[idx]!, ...patch };
    onChange(next);
  };
  const remove = (idx: number) => onChange(thresholds.filter((_, i) => i !== idx));
  const add = () => onChange([...thresholds, nextThreshold(thresholds, maxScore)]);

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border/70 bg-card">
        {thresholds.length === 0 && (
          <p className="px-5 py-8 text-center text-sm italic text-muted-foreground">
            {t("tests.admin.thresholdsHint", { max: maxScore })}
          </p>
        )}
        {thresholds.map((thr, i) => {
          const span = maxScore > 0 ? Math.max(0, thr.maxScore - thr.minScore) / maxScore : 0;
          const offset = maxScore > 0 ? thr.minScore / maxScore : 0;
          return (
            <div
              key={i}
              className="group relative grid grid-cols-[auto_auto_auto_1fr_auto] items-center gap-2 border-b border-border/60 px-4 py-3 last:border-b-0 sm:gap-3"
            >
              <span
                className="hidden w-8 font-heading text-base italic tabular-nums leading-none text-muted-foreground/70 sm:block"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {String.fromCharCode(65 + i)}
              </span>
              <Input
                type="number"
                min={0}
                value={thr.minScore}
                onChange={(e) => update(i, { minScore: Number(e.target.value) })}
                className="h-9 w-16 text-sm tabular-nums"
                placeholder="min"
                aria-label="min"
              />
              <Input
                type="number"
                min={0}
                value={thr.maxScore}
                onChange={(e) => update(i, { maxScore: Number(e.target.value) })}
                className="h-9 w-16 text-sm tabular-nums"
                placeholder="max"
                aria-label="max"
              />
              <div className="flex flex-col gap-1.5">
                <Input
                  type="text"
                  value={thr.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  className="h-9 border-x-0 border-t-0 border-b bg-transparent px-0 text-sm shadow-none focus-visible:border-primary focus-visible:ring-0"
                  placeholder={t("tests.admin.thresholdLabel")}
                />
                <div
                  aria-hidden
                  className="relative h-1 overflow-hidden rounded-full bg-muted/60"
                >
                  <span
                    className="absolute inset-y-0 rounded-full bg-primary/70"
                    style={{
                      left: `${Math.min(100, offset * 100)}%`,
                      width: `${Math.min(100, span * 100)}%`,
                    }}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => remove(i)}
                className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash />
              </Button>
            </div>
          );
        })}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        className="rounded-full"
      >
        <Plus />
        {t("tests.admin.addThreshold")}
      </Button>
    </div>
  );
}
